// app-pages/AdminPage/InstanceStatesPanel.tsx
"use client";
import { Flex, Text,Box, Badge, Table, Thead, Tbody, Tr, Th, Td } from "@chakra-ui/react";
import React from "react";
import {
  useInstanceStates,
  useUserImageGenerationStats,
  useImageGenerationStats,
  useQueueInfo,
  usePodUsageStats,
} from "queries";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,

} from "recharts";
import { TimeWindow, timeWindowOptions } from "conts/TimeWindows";

interface ImageGenerationStat {
  image_id: string;
  user_id: string;
  checkpoint_id: string;
  duration_seconds: number;
  type: string; // 'img2img', 'upscaled', 'normal'
  created_at: string; // ISO date string
  completed_at: string; // ISO date string
}

interface AvgDurationByType {
  type: string;
  averageDuration: number;
  count: number;
}

interface AvgDurationByCheckpoint {
  checkpoint_id: string;
  averageDuration: number;
  count: number;
}

interface DurationOverTimeData {
  time: string;
  averageDuration: number;
  count: number;
}

interface UserImageGenerationStatChart {
  username: string;
  totalImages: number;
  normalGenerations: number;
  remixGenerations: number;
}

interface TooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
}

interface StatusCount {
  status: string;
  count: number;
}

export const InstanceStatesPanel: React.FC = () => {
  const [avgDurationByType, setAvgDurationByType] = React.useState<
    AvgDurationByType[]
  >([]);
  const [avgDurationByCheckpoint, setAvgDurationByCheckpoint] = React.useState<
    AvgDurationByCheckpoint[]
  >([]);
  const [selectedTimeWindow, setSelectedTimeWindow] =
    React.useState<TimeWindow>(TimeWindow.TWENTY_FOUR_HOURS);
  const {
    data: instanceData,
    isLoading: isInstanceLoading,
    error: instanceError,
  } = useInstanceStates();

  const {
    data: queueInfoData,
    isLoading: isQueueInfoLoading,
    error: queueInfoError,
  } = useQueueInfo();

  const {
    data: podUsageStats,
    isLoading: isPodUsageStatsLoading,
    error: podUsageStatsError,
  } = usePodUsageStats();

  const [statusCounts, setStatusCounts] = React.useState<StatusCount[]>([]);
  const {
    data: userImagesStats,
    isLoading: isUserImagesLoading,
    error: userImagesError,
  } = useUserImageGenerationStats(selectedTimeWindow);

  const {
    data: imageGenStats,
    isLoading: isImageGenStatsLoading,
    error: imageGenError,
  } = useImageGenerationStats(selectedTimeWindow);

  React.useEffect(() => {
    if (imageGenStats && imageGenStats.length > 0) {
      const statusCounter: { [key: string]: number } = {};

      imageGenStats.forEach((stat) => {
        if (!statusCounter[stat.status]) {
          statusCounter[stat.status] = 0;
        }
        statusCounter[stat.status] += 1;
      });

      const statusData: StatusCount[] = Object.keys(statusCounter).map(
        (status) => ({
          status,
          count: statusCounter[status],
        })
      );

      setStatusCounts(statusData);
    } else {
      setStatusCounts([]);
    }
  }, [imageGenStats]);

  React.useEffect(() => {
    if (imageGenStats && imageGenStats.length > 0) {
      type TypeDurations = {
        [type: string]: {
          totalDuration: number;
          count: number;
        };
      };

      const typeDurations = imageGenStats.reduce(
        (acc: TypeDurations, stat: ImageGenerationStat) => {
          if (!acc[stat.type]) {
            acc[stat.type] = { totalDuration: 0, count: 0 };
          }
          acc[stat.type].totalDuration += stat.duration_seconds;
          acc[stat.type].count += 1;
          return acc;
        },
        {} as TypeDurations
      );

      const avgDurationData: AvgDurationByType[] = Object.keys(
        typeDurations
      ).map((type) => ({
        type,
        averageDuration:
          typeDurations[type].totalDuration / typeDurations[type].count,
        count: typeDurations[type].count,
      }));

      setAvgDurationByType(avgDurationData);
    } else {
      setAvgDurationByType([]);
    }
  }, [imageGenStats]);

  React.useEffect(() => {
    if (imageGenStats && imageGenStats.length > 0) {
      type CheckpointDurations = {
        [checkpointId: string]: {
          totalDuration: number;
          count: number;
        };
      };

      const checkpointDurations = imageGenStats.reduce(
        (acc: CheckpointDurations, stat: ImageGenerationStat) => {
          if (!acc[stat.checkpoint_id]) {
            acc[stat.checkpoint_id] = { totalDuration: 0, count: 0 };
          }
          acc[stat.checkpoint_id].totalDuration += stat.duration_seconds;
          acc[stat.checkpoint_id].count += 1;
          return acc;
        },
        {} as CheckpointDurations
      );

      const avgDurationData: AvgDurationByCheckpoint[] = Object.keys(
        checkpointDurations
      ).map((checkpoint_id) => ({
        checkpoint_id,
        averageDuration:
          checkpointDurations[checkpoint_id].totalDuration /
          checkpointDurations[checkpoint_id].count,
        count: checkpointDurations[checkpoint_id].count,
      }));

      setAvgDurationByCheckpoint(avgDurationData);
    } else {
      setAvgDurationByCheckpoint([]);
    }
  }, [imageGenStats]);

  const [durationOverTimeData, setDurationOverTimeData] = React.useState<
    DurationOverTimeData[]
  >([]);

  React.useEffect(() => {
    if (imageGenStats && imageGenStats.length > 0) {
      const firstTime = new Date(imageGenStats[0].created_at).getTime();
      const lastTime = new Date(
        imageGenStats[imageGenStats.length - 1].created_at
      ).getTime();
      const totalDurationMinutes = (lastTime - firstTime) / (1000 * 60);
      type TimeBuckets = {
        [timeKey: string]: {
          time: string;
          durations: number[];
          count: number;
        };
      };

      let timeGranularity: string;

      if (totalDurationMinutes <= 60) {
        timeGranularity = "minute";
      } else if (totalDurationMinutes <= 1440) {
        timeGranularity = "hour";
      } else {
        timeGranularity = "day";
      }

      const timeBuckets: TimeBuckets = {};

      imageGenStats.forEach((stat) => {
        let timeKey: string = "";

        const createdAt = new Date(stat.created_at);

        switch (timeGranularity) {
          case "minute":
            timeKey = createdAt.toISOString().substr(0, 16);
            break;
          case "hour":
            timeKey = createdAt.toISOString().substr(0, 13);
            break;
          case "day":
            timeKey = createdAt.toISOString().substr(0, 10);
            break;
          default:
            timeKey = createdAt.toISOString().substr(0, 16);
        }

        if (!timeBuckets[timeKey]) {
          timeBuckets[timeKey] = { time: timeKey, durations: [], count: 0 };
        }
        timeBuckets[timeKey].durations.push(stat.duration_seconds);
        timeBuckets[timeKey].count += 1;
      });

      const data: DurationOverTimeData[] = Object.values(timeBuckets).map(
        (bucket) => ({
          time: bucket.time,
          averageDuration:
            bucket.durations.reduce((a, b) => a + b, 0) /
            bucket.durations.length,
          count: bucket.count,
        })
      );

      // Sort the data by time
      data.sort(
        (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime()
      );

      setDurationOverTimeData(data);
    } else {
      setDurationOverTimeData([]);
    }
  }, [imageGenStats, selectedTimeWindow]);

  // User Image Generation Statistics
  const chartData: UserImageGenerationStatChart[] = userImagesStats
    ? userImagesStats.map((userStat) => ({
        username: userStat.username || userStat.email || userStat.user_id,
        totalImages: userStat.total_images,
        normalGenerations: userStat.normal_generations,
        remixGenerations: userStat.remix_generations,
      }))
    : [];

  const topUsers = chartData.slice(0, 10);

  return (
    <Flex w="100%" direction="column" alignItems="center">
      <Text fontWeight="semibold" fontSize="24px" mt="20px">
        Instance States Panel
      </Text>

      {/* Display errors if any */}
      {queueInfoError && (
        <Text color="red.500" mt="4">
          Error loading queue info: {queueInfoError.message}
        </Text>
      )}
      {instanceError && (
        <Text color="red.500" mt="4">
          Error loading instance states: {instanceError.message}
        </Text>
      )}
      {userImagesError && (
        <Text color="red.500" mt="4">
          Error loading user image generation stats: {userImagesError.message}
        </Text>
      )}
      {imageGenError && (
        <Text color="red.500" mt="4">
          Error loading image generation stats: {imageGenError.message}
        </Text>
      )}
      {podUsageStatsError && (
        <Text color="red.500" mt="4">
          Error loading pod usage stats: {podUsageStatsError.message}
        </Text>
      )}

      {/* Pod Usage Statistics */}
      <Flex w="100%" direction="column" alignItems="flex-start" mt="20px">
        <Text fontWeight="semibold" fontSize="22px" mb="10px">
          Pod Usage Statistics
        </Text>
        {isPodUsageStatsLoading ? (
          <Text>Loading pod usage statistics...</Text>
        ) : podUsageStats ? (
          <>
            <Flex 
              w="100%" 
              mb="20px" 
              p="4"
              bg="gray.100" 
              borderRadius="md"
              color="gray.800"
              flexWrap="wrap"
              gap="4"
            >
              <Box p="3" borderRadius="md" bg="white" boxShadow="sm" flex="1" minW="200px">
                <Text fontSize="sm" color="gray.500">Total Pods</Text>
                <Text fontSize="2xl" fontWeight="bold">{podUsageStats.total_pods}</Text>
              </Box>
              
              <Box p="3" borderRadius="md" bg="white" boxShadow="sm" flex="1" minW="200px">
                <Text fontSize="sm" color="gray.500">Total Generations</Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {podUsageStats.pod_usage_stats && podUsageStats.pod_usage_stats.reduce((sum, pod) => sum + pod.total_generations, 0)}
                </Text>
              </Box>
              
              <Box p="3" borderRadius="md" bg="white" boxShadow="sm" flex="1" minW="200px">
                <Text fontSize="sm" color="gray.500">Active Pods</Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {podUsageStats.pod_usage_stats && podUsageStats.pod_usage_stats.filter(pod => !pod.currently_idle).length}
                </Text>
                <Badge colorScheme="green">Active</Badge>
              </Box>
              
              <Box p="3" borderRadius="md" bg="white" boxShadow="sm" flex="1" minW="200px">
                <Text fontSize="sm" color="gray.500">Idle Pods</Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {podUsageStats.pod_usage_stats && podUsageStats.pod_usage_stats.filter(pod => pod.currently_idle).length}
                </Text>
                <Badge colorScheme="yellow">Idle</Badge>
              </Box>
              
              <Box p="3" borderRadius="md" bg="white" boxShadow="sm" flex="1" minW="200px">
                <Text fontSize="sm" color="gray.500">Avg. Processing Time</Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {podUsageStats.pod_usage_stats && podUsageStats.pod_usage_stats.length > 0 ? 
                    (podUsageStats.pod_usage_stats.reduce((sum, pod) => sum + pod.avg_processing_seconds_per_generation, 0) / 
                      podUsageStats.pod_usage_stats.length
                    ).toFixed(2) + 's'
                    : 'N/A'
                  }
                </Text>
              </Box>
            </Flex>

            <Box w="100%" overflowX="auto">
              <Box mb="10px">
                <Badge colorScheme="blue" fontSize="0.9em" mr="2">Total Pods: {podUsageStats.total_pods}</Badge>
              </Box>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Pod ID</Th>
                    <Th>Total Generations</Th>
                    <Th>Gens/Hour</Th>
                    <Th>Last Activity</Th>
                    <Th>Active %</Th>
                    <Th>Status</Th>
                    <Th>Avg Proc Time</Th>
                    <Th>Completion Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {podUsageStats.pod_usage_stats.map((pod) => (
                    <Tr key={pod.pod_id}>
                      <Td>{pod.pod_id}</Td>
                      <Td isNumeric>{pod.total_generations}</Td>
                      <Td isNumeric>{pod.average_generations_per_hour.toFixed(2)}</Td>
                      <Td>
                        {new Date(pod.last_activity).toLocaleTimeString()} 
                        <Badge ml="1" colorScheme={pod.last_activity_minutes_ago < 5 ? "green" : "orange"}>
                          {pod.last_activity_minutes_ago.toFixed(1)} min ago
                        </Badge>
                      </Td>
                      <Td isNumeric>{pod.active_time_percentage.toFixed(2)}%</Td>
                      <Td>
                        <Badge colorScheme={pod.currently_idle ? "yellow" : "green"}>
                          {pod.currently_idle ? "Idle" : "Active"}
                        </Badge>
                      </Td>
                      <Td isNumeric>{pod.avg_processing_seconds_per_generation.toFixed(2)}s</Td>
                      <Td>
                        <Flex direction="column">
                          {pod.status_distribution && Object.entries(pod.status_distribution).map(([status, count]) => (
                            <Text key={status} fontSize="xs">
                              {status}: {count}
                            </Text>
                          ))}
                        </Flex>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>

            <Flex w="100%" mt="20px" justifyContent="space-between" flexWrap="wrap">
              {/* Generations per hour chart */}
              <Box width={{ base: "100%", xl: "48%" }} mb="20px">
                <Text fontWeight="semibold" fontSize="18px" mb="10px">
                  Generations Per Hour by Pod
                </Text>
                {podUsageStats.pod_usage_stats && podUsageStats.pod_usage_stats.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={podUsageStats.pod_usage_stats}>
                      <XAxis dataKey="pod_id" />
                      <YAxis 
                        label={{ 
                          value: "Generations/Hour", 
                          angle: -90, 
                          position: "insideLeft" 
                        }} 
                      />
                      <Tooltip formatter={(value: number) => value.toFixed(2)} />
                      <Bar 
                        dataKey="average_generations_per_hour" 
                        fill="#8884d8"
                        name="Generations Per Hour"
                      >
                        <LabelList 
                          dataKey="average_generations_per_hour" 
                          position="top" 
                          formatter={(value: number) => value.toFixed(1)} 
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Text>No data available</Text>
                )}
              </Box>

              {/* Average processing time chart */}
              <Box width={{ base: "100%", xl: "48%" }} mb="20px">
                <Text fontWeight="semibold" fontSize="18px" mb="10px">
                  Average Processing Time by Pod
                </Text>
                {podUsageStats.pod_usage_stats && podUsageStats.pod_usage_stats.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={podUsageStats.pod_usage_stats}>
                      <XAxis dataKey="pod_id" />
                      <YAxis 
                        label={{ 
                          value: "Processing Time (s)", 
                          angle: -90, 
                          position: "insideLeft" 
                        }} 
                      />
                      <Tooltip formatter={(value: number) => value.toFixed(2) + "s"} />
                      <Bar 
                        dataKey="avg_processing_seconds_per_generation" 
                        fill="#82ca9d"
                        name="Avg Processing Time (s)"
                      >
                        <LabelList 
                          dataKey="avg_processing_seconds_per_generation" 
                          position="top" 
                          formatter={(value: number) => value.toFixed(1)} 
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Text>No data available</Text>
                )}
              </Box>
            </Flex>

            <Flex w="100%" mt="10px" justifyContent="space-between" flexWrap="wrap">
              {/* Active Time Percentage chart */}
              <Box width={{ base: "100%", xl: "48%" }} mb="20px">
                <Text fontWeight="semibold" fontSize="18px" mb="10px">
                  Active Time Percentage by Pod
                </Text>
                {podUsageStats.pod_usage_stats && podUsageStats.pod_usage_stats.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={podUsageStats.pod_usage_stats}>
                      <XAxis dataKey="pod_id" />
                      <YAxis 
                        label={{ 
                          value: "Active Time (%)", 
                          angle: -90, 
                          position: "insideLeft" 
                        }} 
                      />
                      <Tooltip formatter={(value: number) => value.toFixed(2) + "%"} />
                      <Bar 
                        dataKey="active_time_percentage" 
                        fill="#ffc658"
                        name="Active Time (%)"
                      >
                        <LabelList 
                          dataKey="active_time_percentage" 
                          position="top" 
                          formatter={(value: number) => value.toFixed(1) + "%"} 
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Text>No data available</Text>
                )}
              </Box>

              {/* Total Generations chart */}
              <Box width={{ base: "100%", xl: "48%" }} mb="20px">
                <Text fontWeight="semibold" fontSize="18px" mb="10px">
                  Total Generations by Pod
                </Text>
                {podUsageStats.pod_usage_stats && podUsageStats.pod_usage_stats.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={podUsageStats.pod_usage_stats}>
                      <XAxis dataKey="pod_id" />
                      <YAxis 
                        label={{ 
                          value: "Total Generations", 
                          angle: -90, 
                          position: "insideLeft" 
                        }} 
                      />
                      <Tooltip />
                      <Bar 
                        dataKey="total_generations" 
                        fill="#ff8042"
                        name="Total Generations"
                      >
                        <LabelList 
                          dataKey="total_generations" 
                          position="top" 
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Text>No data available</Text>
                )}
              </Box>
            </Flex>
          </>
        ) : (
          <Text>No pod usage data available.</Text>
        )}
      </Flex>

   

    </Flex>
  );
};

const CustomTooltipType = ({ active, payload, label }: TooltipProps) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;

    return (
      <Box backgroundColor="#fff" border="1px solid #ccc" p="2" color="#000">
        <p>
          <strong>Type:</strong> {data.type}
        </p>
        <p>
          <strong>Average Duration:</strong> {data.averageDuration.toFixed(2)} s
        </p>
        <p>
          <strong>Total Generations:</strong> {data.count}
        </p>
      </Box>
    );
  }

  return null;
};

const CustomTooltipCheckpoint = ({ active, payload, label }: TooltipProps) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;

    return (
      <Box backgroundColor="#fff" border="1px solid #ccc" p="2" color="#000">
        <p>
          <strong>Checkpoint ID:</strong> {data.checkpoint_id}
        </p>
        <p>
          <strong>Average Duration:</strong> {data.averageDuration.toFixed(2)} s
        </p>
        <p>
          <strong>Total Generations:</strong> {data.count}
        </p>
      </Box>
    );
  }

  return null;
};

const CustomTooltipOverTime = ({ active, payload, label }: TooltipProps) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;

    let timeFormatted: string;
    const time = new Date(data.time);
    if (data.time.length === 16) {
      timeFormatted = time.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else if (data.time.length === 13) {
      timeFormatted = time.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else {
      // Day granularity
      timeFormatted = time.toLocaleDateString();
    }

    return (
      <Box backgroundColor="#fff" border="1px solid #ccc" p="2" color="#000">
        <p>
          <strong>Time:</strong> {timeFormatted}
        </p>
        <p>
          <strong>Average Duration:</strong> {data.averageDuration.toFixed(2)} s
        </p>
        <p>
          <strong>Total Generations:</strong> {data.count}
        </p>
      </Box>
    );
  }

  return null;
};

export default InstanceStatesPanel;
